import React, { useState } from "react";
import { useFormikContext } from "formik";
import SectionBezel from "components/SectionBezel";
import { getIconForUID } from "components/FormerEditor/utils";
import { TAX_ID_COLLECTION_TYPE_OPTIONS } from "utils/constants/taxRate";
import {
  CHECKOUT_SESSION,
  VIRTUAL_CHECKOUT_SESSION
} from "utils/constants/stripe";
import CheckoutAttributeToggle from "components/CheckoutAttributeToggle";
import { getCheckoutAttributesConfig } from "utils/checkout/attributes";
import TaxIdCollectionType from "./TaxIdCollectionType";
import AutoTaxToggle from "./AutoTaxToggle";
import TaxRatePicker from "./TaxRatePicker";
import { useShortLinkTaxes } from "utils/hooks/shortLink";
import AutoTaxSummary from "./AutoTaxSummary";
import TaxRatesSummary from "./TaxRatesSummary";
import TaxIdCollectionSummary from "./TaxIdCollectionSummary";

const NODE_KEYS = {
  INHERITED_AUTO_TAXES: "inherited-auto-taxes",
  AUTO_TAXES: "auto-taxes",
  INHERITED_TAX_RATES: "inherited-tax-rates",
  TAX_RATES: "destinations",
  INHERITED_TAX_ID_COLLECTION: "inherited-tax-id-collection",
  TAX_ID_COLLECTION: "tax-id-collection"
};

const TaxRatesFields = ({
  name,
  inherited,
  collectionTypeOptions = TAX_ID_COLLECTION_TYPE_OPTIONS
}) => {
  const [open, setOpen] = useState(false);
  const { values } = useFormikContext();
  const canEdit = !Boolean(values.id);
  const LockIcon = getIconForUID("lock");

  const {
    autoTaxError,
    taxRatesError,
    inheritedAutoTaxes,
    inheritedTaxIdType,
    inheritedTaxIdTypeEnabled,
    preparedInheritedTaxRates,
    showInheritedAutoTaxes,
    showInheritedTaxRates,
    showInheritedTaxId
  } = useShortLinkTaxes({
    name,
    inherited
  });

  const toggleProps = {
    attributes: getCheckoutAttributesConfig({
      name,
      attributes: [
        CHECKOUT_SESSION.TAX_ID_COLLECTION,
        CHECKOUT_SESSION.AUTOMATIC_TAX,
        VIRTUAL_CHECKOUT_SESSION.TAX_RATES,
        VIRTUAL_CHECKOUT_SESSION.DYNAMIC_TAX_RATES
      ]
    }),
    open,
    onChange: (updatedOpen) => updatedOpen && setOpen(updatedOpen)
  };
  const action = !canEdit ? (
    <LockIcon size={18} />
  ) : (
    <CheckoutAttributeToggle {...toggleProps} />
  );

  let nodes = [];

  if (showInheritedAutoTaxes) {
    nodes.push({
      key: NODE_KEYS.INHERITED_AUTO_TAXES,
      node: (
        <div className="pb3">
          <AutoTaxSummary active={inheritedAutoTaxes} error={autoTaxError} />
        </div>
      )
    });
  } else {
    nodes.push({
      key: NODE_KEYS.AUTO_TAXES,
      node: <AutoTaxToggle />
    });
  }

  /**
   * Inherited tax rates are active when
   * - tax rates is defined
   * - auto taxes is false
   * - values auto tax is false
   */
  if (showInheritedTaxRates) {
    nodes.push({
      key: NODE_KEYS.INHERITED_TAX_RATES,
      node: (
        <div className="pb3">
          <TaxRatesSummary
            error={taxRatesError}
            options={preparedInheritedTaxRates}
          />
        </div>
      )
    });
  } else if (!showInheritedAutoTaxes) {
    nodes.push({
      key: NODE_KEYS.TAX_RATES,
      node: <TaxRatePicker />
    });
  }

  if (showInheritedTaxId) {
    nodes.push({
      key: NODE_KEYS.INHERITED_TAX_ID_COLLECTION,
      node: (
        <TaxIdCollectionSummary
          type={inheritedTaxIdType}
          active={Boolean(inheritedTaxIdTypeEnabled)}
        />
      )
    });
  } else {
    nodes.push({
      key: NODE_KEYS.TAX_ID_COLLECTION,
      node: <TaxIdCollectionType options={collectionTypeOptions} />
    });
  }

  return (
    <SectionBezel
      header="Taxes"
      action={action}
      expandable
      isOpen={open}
      setOpen={setOpen}
    >
      {nodes.map(({ node, key }) => (
        <div key={key} className="w-100">
          {node}
        </div>
      ))}
    </SectionBezel>
  );
};

export default TaxRatesFields;
